/**
 * LinkedCell - Phase H + Phase #6.
 *
 * The "Linked" column for both Review-Needed and Completed views.
 * Visual states:
 *   • Bank feed MATCHED the txn to an existing user document (invoice /
 *     bill / receipt / payment) → solid emerald "Linked" pill with the
 *     document ref. This is what the user thinks of as "Linked".
 *   • Bank feed AUTO-CREATED a document for the txn because no matching
 *     one existed → subtle gray "Auto-created" pill with a Sparkles
 *     icon. Visually distinct so the user doesn't confuse it with a
 *     true match. (Iteration 703 part 7 - user explicitly clarified
 *     that "Linked" should mean matched-to-existing only.)
 *   • Nothing → muted "Link" button to open the manual link dialog.
 *
 * Each pill exposes a dropdown for View / Unlink / Replace.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { Link2, Unlink, ExternalLink, Receipt, ChevronDown, Sparkles, CalendarDays } from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

const DOC_ROUTE = {
  invoice: (id) => `/invoices/${id}/edit`,
  bill: (id) => `/bills/${id}/edit`,
  other_receipt: (id) => `/other-receipts/${id}`,
  other_payment: (id) => `/other-payments/${id}`,
  loan: (id) => `/loans/${id}/edit`,
  investment: (id) => `/investments/${id}/edit`,
  fixed_asset: (id) => `/fixed-assets/${id}/edit`,
  prepayment: (id) => `/prepaid-expenses?edit=${id}`,
};

const DOC_LABEL = {
  invoice: 'Invoice',
  bill: 'Bill',
  other_receipt: 'Receipt',
  other_payment: 'Payment',
  loan: 'Loan',
  investment: 'Investment',
  fixed_asset: 'Fixed Asset',
  prepayment: 'Prepayment',
};

export const LinkedCell = ({ tx, onUnlink, onOpenLink }) => {
  const navigate = useNavigate();
  // True match (existing doc the feed found via matching rules)
  const matchedId = tx.matched_document_id;
  const matchedType = tx.matched_document_type;
  const matchedRef = tx.matched_document_ref;
  // Auto-created (the feed itself created the doc because nothing matched)
  const createdId = tx.created_document_id;
  const createdType = tx.created_document_type;
  const createdRef = tx.created_document_ref;

  const isMatched = !!matchedId;
  const isCreated = !!createdId && !isMatched;
  const linkedId = matchedId || createdId;
  const linkedType = matchedType || createdType;
  const linkedRef = matchedRef || createdRef;

  if (!linkedId) {
    // Unlinked → tiny "Link" button.
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => onOpenLink?.(tx)}
        className="h-7 px-2 text-[11px] gap-1 text-[#108981] hover:bg-[#108981]/10"
        data-testid={`link-btn-${tx.id}`}
      >
        <Link2 className="w-3 h-3" />
        Link
      </Button>
    );
  }

  const route = DOC_ROUTE[linkedType];
  const label = DOC_LABEL[linkedType] || 'Doc';

  // Different chip styling for matched vs auto-created so the user
  // immediately knows whether the bank feed found an existing document
  // or created one from scratch.
  const triggerClasses = isMatched
    ? 'text-[10px] gap-1 cursor-pointer hover:bg-emerald-100 truncate max-w-[120px] border-emerald-200 bg-emerald-50 text-emerald-800'
    : 'text-[10px] gap-1 cursor-pointer hover:bg-violet-100 truncate max-w-[130px] border-violet-200 bg-violet-50 text-violet-800';
  const Icon = isMatched ? Receipt : Sparkles;
  const preview = tx.linked_doc_preview || null;
  const previewTotal = preview?.total != null
    ? formatCurrency(preview.total, preview.currency || tx.currency) : null;
  const previewOverdue = preview?.status === 'overdue';
  const triggerText = isMatched
    ? (linkedRef || preview?.ref || label)
    : `Auto · ${linkedRef || preview?.ref || label}`;
  const previewTitle = preview
    ? `${previewTotal ? ` · ${previewTotal}` : ''}${preview.due_date ? ` · due ${preview.due_date}` : ''}`
    : '';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex flex-col items-start gap-0.5 max-w-full"
          data-testid={`linked-trigger-${tx.id}`}
          title={isMatched
            ? `Matched to existing ${label}${linkedRef ? ' ' + linkedRef : ''}${previewTitle}`
            : `Auto-created ${label}${linkedRef ? ' ' + linkedRef : ''}${previewTitle}. There was no existing document to match`}
        >
          <Badge variant="outline" className={triggerClasses}>
            <Icon className="w-3 h-3" />
            <span className="truncate">{triggerText}</span>
            <ChevronDown className="w-3 h-3 opacity-60" />
          </Badge>
          {preview && (previewTotal || preview.due_date) && (
            <span
              className="flex items-center gap-1 text-[9px] text-muted-foreground whitespace-nowrap pl-0.5"
              data-testid={`linked-preview-${tx.id}`}
            >
              {previewTotal && <span className="font-semibold text-foreground/80">{previewTotal}</span>}
              {preview.due_date && (
                <span className={`inline-flex items-center gap-0.5 ${previewOverdue ? 'text-red-600 font-semibold' : ''}`}>
                  <CalendarDays className="w-2.5 h-2.5" />
                  {previewOverdue ? 'Overdue ' : 'Due '}{preview.due_date}
                </span>
              )}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {/* Header explains origin */}
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
          {isMatched ? 'Matched to existing doc' : 'Auto-created by bank feed'}
        </div>
        {preview && (
          <div className="px-2 pb-1.5 text-[11px] text-foreground/80 space-y-0.5" data-testid={`linked-preview-detail-${tx.id}`}>
            {preview.counterparty && <div className="truncate">{preview.counterparty}</div>}
            {previewTotal && <div>Total: <b>{previewTotal}</b></div>}
            {preview.balance_due != null && preview.balance_due > 0 && (
              <div>Outstanding: <b>{formatCurrency(preview.balance_due, preview.currency || tx.currency)}</b></div>
            )}
            {preview.due_date && <div className={previewOverdue ? 'text-red-600' : ''}>{previewOverdue ? 'Overdue since' : 'Due'} {preview.due_date}</div>}
          </div>
        )}
        <DropdownMenuSeparator />
        {route ? (
          <DropdownMenuItem
            onClick={() => navigate(route(linkedId))}
            data-testid={`linked-view-${tx.id}`}
            className="gap-2 text-xs"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Open {label.toLowerCase()}
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem disabled className="gap-2 text-xs opacity-60">
            <ExternalLink className="w-3.5 h-3.5" />
            Open not available
          </DropdownMenuItem>
        )}
        {/* iter1411 · Replace-linkage CTA. Always shown for In-Books rows
            (both matched-to-existing AND auto-created states). Opens the
            SmartLinkDocumentModal in relink mode which atomically tears
            down the prior journal + doc linkage and posts a fresh
            composite settlement against the newly-picked docs. */}
        <DropdownMenuItem
          onClick={() => onOpenLink?.(tx, { mode: 'relink' })}
          data-testid={`linked-relink-${tx.id}`}
          className="gap-2 text-xs text-amber-700 focus:text-amber-800"
        >
          <Link2 className="w-3.5 h-3.5" />
          Link to existing document…
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => onUnlink?.(tx)}
          data-testid={`linked-unlink-${tx.id}`}
          className="gap-2 text-xs text-orange-700 focus:text-orange-700"
        >
          <Unlink className="w-3.5 h-3.5" />
          {isCreated ? 'Send back to Review' : 'Unlink'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LinkedCell;
